import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getStripePlans } from "../api";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { MODULE_LABELS, MODULE_COLORS, ModuleKey } from "../moduleMetadata";
import { MODULE_PITCH } from "../moduleCatalogue";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const PLAN_LABEL: Record<string, string> = {
  starter: "Starter", growth: "Growth", pro: "Pro",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** The meters a plan card spells out, in the order they are shown.
 *
 * All three are CONSUMPTION_METERS, which is what makes the plain addition in
 * `cardLimits` correct — the CAP_METERS (employees, screening_*) combine by max,
 * and a card that summed those would overstate them. `test_signup_card_quota.py`
 * asserts this set stays additive so the two cannot drift apart.
 */
const CARD_METERS = [
  { key: "cv_processed", label: (n: number) => `${n.toLocaleString()} CVs/mo` },
  { key: "ai_operation", label: (n: number) => `${n.toLocaleString()} AI ops/mo` },
  { key: "ai_doc",       label: (n: number) => `${n.toLocaleString()} AI docs/mo` },
] as const;

/** Core's allowance plus whatever `modules` add — the basket actually being bought.
 *
 * A tier buys Core alone: CVs are a Recruitment allowance and part of the AI ops
 * and docs are Finance's and Recruitment's. Reading `p.limits` here (the
 * full-catalogue table) is what made the card promise 400 CVs/mo to a customer
 * who ticked no modules and would get zero.
 */
function cardLimits(p: any, moduleLimits: any, plan: string, modules: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const { key } of CARD_METERS) {
    out[key] = modules.reduce(
      (sum, m) => sum + (moduleLimits?.[m]?.[plan]?.[key] ?? 0),
      p.core_limits?.[key] ?? 0,
    );
  }
  return out;
}

function planFeatures(p: any, limits: Record<string, number>): string[] {
  return [
    `${p.included_employees} employees included`,
    ...CARD_METERS.filter(({ key }) => limits[key] > 0).map(({ key, label }) => label(limits[key])),
    `+$${p.overage_rate_usd}/extra employee`,
  ];
}

const VALID_PLAN_IDS = ["starter", "growth", "pro"];

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan");
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [selectedPlan, setSelectedPlan]   = useState(
    requestedPlan && VALID_PLAN_IDS.includes(requestedPlan) ? requestedPlan : "growth"
  );
  const [selectedModules, setSelectedModules] = useState<ModuleKey[]>([]);
  const [modulesOpen,     setModulesOpen]     = useState(false);
  const [companyName,  setCompanyName]    = useState("");
  const [domain,       setDomain]         = useState("");
  const [contactEmail, setContactEmail]   = useState("");
  const [loading,      setLoading]        = useState(false);
  const [error,        setError]          = useState("");

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ["stripe-plans"], queryFn: getStripePlans,
    staleTime: 5 * 60 * 1000, retry: false,
  });
  const plans: any[] = plansData?.plans ?? [];
  const plan = plans.find(p => p.id === selectedPlan);

  // Only modules the backend can actually charge for at this tier. A module with
  // no configured price renders no control at all rather than one that 400s.
  const availableModules: ModuleKey[] = plansData?.modules?.[selectedPlan] ?? [];

  // Derived, not stored: switching tier can withdraw a module, and a stale
  // selection would then be posted to a checkout that rejects it.
  const activeModules = selectedModules.filter(m => availableModules.includes(m));

  // Prices come from the backend, never hardcoded here — a number written into this
  // component is a number that will disagree with Stripe within a quarter.
  const modulePrices = plansData?.module_prices ?? {};
  const modulePrice = (key: ModuleKey): number | null =>
    modulePrices?.[key]?.[selectedPlan]?.[billingPeriod] ?? null;

  // Everything shown per-month, including annual, so the tiers stay comparable.
  const perMonth = (usd: number | null): number | null =>
    usd === null ? null : billingPeriod === "annual" ? Math.round(usd / 12) : usd;

  const modulesMonthly = activeModules.reduce(
    (sum, key) => sum + (perMonth(modulePrice(key)) ?? 0), 0,
  );

  function toggleModule(key: ModuleKey) {
    setSelectedModules(prev =>
      prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
    );
  }

  async function handleSubmit() {
    if (!EMAIL_RE.test(contactEmail.trim())) {
      setError("Enter a valid email address");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/stripe/register-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name:   companyName,
          domain:         domain.replace(/^@/, "").trim(),
          contact_email:  contactEmail,
          plan:           selectedPlan,
          billing_period: billingPeriod,
          modules:        activeModules,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.detail || "Something went wrong");
        return;
      }
      window.location.href = d.checkout_url;
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (plansLoading || !plan) {
    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${NAVY} 0%, #0f172a 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--font-sans)", padding: "32px 16px",
      }}>
        <div style={{ color: "white", fontSize: 14 }}>Loading plans…</div>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
    border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
    color: "#1e293b", marginBottom: 12,
  };

  const price = billingPeriod === "monthly" ? plan.monthly_price_usd : Math.round(plan.annual_price_usd / 12);

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${NAVY} 0%, #0f172a 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "var(--font-sans)", padding: "32px 16px",
    }}>
      <LoadingOverlay active={loading} />
      <div style={{
        background: "white", borderRadius: 16, padding: "40px 36px",
        width: "min(520px, calc(100vw - 32px))",
        boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/favicon.png" alt="HRxAI" style={{
            width: 56, height: 56, borderRadius: 12, objectFit: "contain",
            margin: "0 auto 14px", display: "block",
          }} />
          <h1 style={{ color: NAVY, fontSize: 22, fontWeight: 800, margin: "0 0 2px" }}>
            HRx<span style={{ color: TEAL }}>AI</span>
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>Start your subscription</p>
        </div>

        {/* Billing toggle */}
        <div style={{
          display: "flex", background: "#f1f5f9", borderRadius: 10,
          padding: 4, marginBottom: 20, gap: 4,
        }}>
          {(["monthly", "annual"] as const).map(p => (
            <button
              key={p}
              onClick={() => setBillingPeriod(p)}
              style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none",
                background: billingPeriod === p ? "white" : "transparent",
                color: billingPeriod === p ? NAVY : "#64748b",
                fontWeight: billingPeriod === p ? 700 : 500,
                fontSize: 13, cursor: "pointer",
                boxShadow: billingPeriod === p ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {p === "monthly" ? "Monthly" : (
                <>Annual <span style={{ color: TEAL, fontWeight: 700 }}>Save 17%</span></>
              )}
            </button>
          ))}
        </div>

        {/* Plan cards. The class carries the mobile stack rule — see responsive.css;
            three of these across a 375px viewport is unreadable. */}
        <div className="signup-plan-row" style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          {plans.map(p => {
            const isSelected = selectedPlan === p.id;
            const displayPrice = billingPeriod === "monthly" ? p.monthly_price_usd : Math.round(p.annual_price_usd / 12);
            // Per card, not per selection: switching tier can withdraw a module, so a
            // card must price the basket buyable at *its* tier, not the current one.
            const cardModules = selectedModules.filter(
              m => (plansData?.modules?.[p.id] ?? []).includes(m)
            );
            const limits = cardLimits(p, plansData?.module_limits, p.id, cardModules);
            // A meter no ticked module supplies is omitted rather than shown as "0
            // CVs/mo". The hint below says where it comes from, so the absence reads
            // as "buy the module" instead of "this tier gives you nothing".
            const hasLockedMeter = CARD_METERS.some(({ key }) => !(limits[key] > 0));
            return (
              <div
                key={p.id}
                onClick={() => setSelectedPlan(p.id)}
                style={{
                  flex: 1, border: `2px solid ${isSelected ? NAVY : "#e2e8f0"}`,
                  borderRadius: 12, padding: "14px 10px", cursor: "pointer", textAlign: "center",
                  background: isSelected ? "#eff6ff" : "white",
                  transition: "all 0.15s",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? NAVY : "#374151", marginBottom: 4 }}>
                  {PLAN_LABEL[p.id] ?? p.id}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: isSelected ? NAVY : "#1e293b" }}>
                  ${displayPrice}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>/mo</div>
                {planFeatures(p, limits).map(f => (
                  <div key={f} style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>{f}</div>
                ))}
                {hasLockedMeter && (
                  <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
                    + module quotas below
                  </div>
                )}
                {isSelected && (
                  <div style={{
                    marginTop: 8, background: NAVY, color: "white",
                    borderRadius: 6, padding: "2px 0", fontSize: 11, fontWeight: 700,
                  }}>Selected</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add-on modules — collapsed, nothing pre-selected. Core alone is the
            expected common case; modules can be added any time from Settings. */}
        {availableModules.length > 0 && (
          <div style={{
            border: "1px solid #e2e8f0", borderRadius: 10,
            marginBottom: 20, overflow: "hidden",
          }}>
            <button
              type="button"
              onClick={() => setModulesOpen(o => !o)}
              aria-expanded={modulesOpen}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "12px 14px", minHeight: 44, background: "#f8fafc",
                border: "none", cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: "#334155", flex: 1 }}>
                Add modules{" "}
                <span style={{ fontWeight: 500, color: "#64748b" }}>(optional)</span>
              </span>
              {activeModules.length > 0 && (
                <span style={{
                  background: NAVY, color: "white", borderRadius: 999,
                  padding: "2px 8px", fontSize: 11, fontWeight: 700,
                }}>
                  {activeModules.length}
                </span>
              )}
              <span style={{ color: "#64748b", fontSize: 13 }}>{modulesOpen ? "▲" : "▼"}</span>
            </button>

            {modulesOpen && (
              <div style={{ padding: "6px 14px 12px" }}>
                <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 10px" }}>
                  Everything below is optional — you can add or remove modules later
                  from Settings.
                </p>
                {availableModules.map(key => {
                  const checked = activeModules.includes(key);
                  return (
                    <label
                      key={key}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "9px 10px", minHeight: 44, borderRadius: 8,
                        cursor: "pointer", boxSizing: "border-box",
                        border: `1.5px solid ${checked ? NAVY : "#e2e8f0"}`,
                        background: checked ? "#eff6ff" : "white",
                        marginBottom: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModule(key)}
                        style={{ marginTop: 2, width: 16, height: 16, accentColor: NAVY, flexShrink: 0 }}
                      />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{
                          display: "block", fontSize: 13, fontWeight: 700,
                          color: checked ? NAVY : "#334155",
                        }}>
                          <span style={{
                            display: "inline-block", width: 8, height: 8, borderRadius: 2,
                            background: MODULE_COLORS[key], marginRight: 7, verticalAlign: "middle",
                          }} />
                          {MODULE_LABELS[key]}
                        </span>
                        <span style={{ display: "block", fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
                          {MODULE_PITCH[key]}
                        </span>
                      </span>
                      <span style={{
                        fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
                        color: checked ? NAVY : "#334155",
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {perMonth(modulePrice(key)) !== null ? `+$${perMonth(modulePrice(key))}/mo` : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Company form */}
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
            padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626",
          }}>
            {error}
          </div>
        )}

        <input style={inputStyle} type="text" placeholder="Company name"
          value={companyName} autoFocus onChange={e => setCompanyName(e.target.value)} />

        <input style={inputStyle} type="text" placeholder="Company domain  (e.g. acme.com)"
          value={domain} onChange={e => setDomain(e.target.value)} />

        <input style={inputStyle} type="email" placeholder="Your work email"
          value={contactEmail} onChange={e => setContactEmail(e.target.value)} />

        {billingPeriod === "annual" && (
          <p style={{ fontSize: 12, color: "#64748b", margin: "-4px 0 12px", textAlign: "center" }}>
            Billed annually · ${plan.annual_price_usd}/yr (2 months free)
          </p>
        )}

        <button
          style={{
            width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
            background: !companyName || !domain || !contactEmail || loading
              ? "#94a3b8"
              : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
            color: "white", fontSize: 15, fontWeight: 700,
            cursor: !companyName || !domain || !contactEmail || loading ? "not-allowed" : "pointer",
            marginTop: 4,
          }}
          disabled={!companyName || !domain || !contactEmail || loading}
          onClick={handleSubmit}
        >
          {loading
            ? "Redirecting…"
            // Core plus every selected module. Quoting Core alone understated a basket
            // with modules in it, which was the right call only while module prices
            // did not exist yet.
            : `Subscribe — $${price + modulesMonthly}/mo →`}
        </button>

        <p style={{ color: "#64748b", fontSize: 13, marginTop: 16, textAlign: "center" }}>
          Already have an account?{" "}
          <a href="/login" style={{ color: TEAL, fontWeight: 600, textDecoration: "none" }}>Sign in</a>
        </p>
        <p style={{ color: "#94a3b8", fontSize: 11, marginTop: 8, textAlign: "center" }}>
          You'll be redirected to Stripe to securely add a card. It's authorized but not
          charged until your 14-day trial ends, and your workspace is created instantly.
        </p>
        <p style={{ color: "#94a3b8", fontSize: 11, marginTop: 6, textAlign: "center" }}>
          No company card handy?{" "}
          <a href="/request-access" style={{ color: TEAL, fontWeight: 600, textDecoration: "none" }}>Request access instead</a>
        </p>
      </div>
    </div>
  );
}
